import Kitsu from "kitsu"

export default class LineSuggestionsApiService {
  constructor(buuid, campaignId) {
    this.buuid = buuid
    this.campaignId = campaignId
    this.currentRequest = null

    this.api = new Kitsu({
      baseURL: `/${buuid}/api`,
      headers: {
        'Accept': 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json'
      }
    })
  }

  async fetchLineSuggestions(params = {}) {
    if (this.currentRequest) {
      this.currentRequest.abort()
    }

    const url = new URL(ssbRoutes('api_v2_campaign_line_suggestions_path', {
      buuid: this.buuid,
      campaign_id: this.campaignId
    }), window.location.origin)

    this._applyParams(url, params)

    const controller = new AbortController()
    this.currentRequest = controller

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: controller.signal
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error:', errorText)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      return this._transformApiResponse(data)
    } finally {
      this.currentRequest = null
    }
  }

  async addLines(selectedLines, autoPrices, customValues) {
    const payload = {
      "suggestions": selectedLines,
      "autoPrices": autoPrices,
      "customValues": customValues
    }

    const response = await fetch(ssbRoutes('api_v2_campaign_line_suggestions_path', {
      buuid: this.buuid,
      campaign_id: this.campaignId
    }), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorData = await response.json()
      const errorMessage = errorData.errors?.[0]?.detail || `HTTP ${response.status}: ${response.statusText}`
      throw new Error(errorMessage)
    }

    return await response.json()
  }

  _applyParams(url, params) {
    if (params.sort) {
      url.searchParams.set('sort', params.sort)
    }

    if (params.page) {
      url.searchParams.set('page[number]', params.page.number)
      url.searchParams.set('page[size]', params.page.size)
    }

    if (params.filter) {
      Object.keys(params.filter).forEach(key => {
        if (params.filter[key]) {
          url.searchParams.set(`filter[${key}]`, params.filter[key])
        }
      })
    }
  }

  _transformApiResponse(response) {
    const lineSuggestions = response.data.map(item => ({
      id: item.id,
      appName: item.attributes.appName || 'Unknown App',
      appId: item.attributes.appId,
      categoryId: item.attributes.categoryId,
      exchangeId: item.attributes.exchangeId,
      trafficNumber: item.attributes.trafficNumber || 0,
      newTraffic: !!(item.attributes.discoveredAt &&
        moment.utc(item.attributes.discoveredAt).isSameOrAfter(moment.utc().subtract(30, 'days'))),
      adType: item.attributes.adType,
      impType: item.attributes.impType,
      appIcon: item.attributes.appIcon,
      planStatus: item.attributes.planStatus || 'never',
      blocked: item.attributes.blocked || false,
      dealId: item.attributes.dealId,
      dealName: item.attributes.dealName,
      tagName: item.attributes.tagName
    }))

    return {
      lineSuggestions,
      totalCount: response.meta?.totalCount || lineSuggestions.length,
      categoryCounts: response.meta?.categoryCounts,
      exchangeCounts: response.meta?.exchangeCounts,
      adTypeCounts: response.meta?.adTypeCounts,
      impTypeCounts: response.meta?.impTypeCounts,
      availableAdTypes: response.meta?.availableAdTypes || []
    }
  }
}
